import { GridColDef } from '@mui/x-data-grid-pro';
import { Dayjs } from 'dayjs';
import { useEffect, useMemo, useRef, type JSX } from 'react';

import { BonusCampaignType, BonusTotals, SingleBonusReport } from '../../../@types/api';
import API_ROUTES from '../../../constants/apiRoutes';
import { appendTotalsRow, getTotalsRowClass } from '../../../helpers';
import { createNumberColumn } from '../../../helpers/columns';
import { usePagination } from '../../../hooks/usePagination';
import { createColumn, formatSnakeCaseString } from '../../../utils';
import TableTemplate from '../../templates/TableTemplate';

export type CampaignBonusReportsPageProps = {
  bonusType: BonusCampaignType;
  date: { fromTimestamp: Dayjs; toTimestamp: Dayjs };
  headerContent: JSX.Element;
};

const getMobileCardHeaderItems = (report: SingleBonusReport) => [
  { label: 'Type', value: formatSnakeCaseString(report.type), flex: 1 },
  { label: 'Distributed', value: Number(report.distributedBonusAmount).toFixed(2), flex: 1 },
  { label: 'Profit', value: Number(report.profit).toFixed(2), flex: 1 },
];

const generateTotalsRow = (data?: BonusTotals) => {
  if (data) {
    return {
      id: 'bonus-report-totals',
      type: 'totals',
      isTotalsRow: true,
      distributedBonusAmount: data.distributedBonusAmount ?? 0,
      stakeAmount: data.stakeAmount ?? 0,
      winningAmount: data.winningAmount ?? 0,
      profit: data.profit ?? 0,
      ticketCount: data.ticketCount ?? 0,
    };
  }
};

const CampaignBonusReportsPage = ({ bonusType, date, headerContent }: CampaignBonusReportsPageProps) => {
  const fromTimestamp = date.fromTimestamp.valueOf();
  const toTimestamp = date.toTimestamp.valueOf();
  const hasSyncedDate = useRef(false);
  const hasSyncedBonusType = useRef(false);
  const { data, updateQueryParams, isFetching, isLoading, changeQuery } = usePagination<SingleBonusReport>(
    API_ROUTES.REPORTS.BONUSES,
    {
      page: 1,
      limit: 25,
      fromTimestamp,
      toTimestamp,
      type: bonusType,
    }
  );

  useEffect(() => {
    if (!hasSyncedDate.current) {
      hasSyncedDate.current = true;
      return;
    }

    changeQuery({
      fromTimestamp,
      toTimestamp,
    });
  }, [changeQuery, fromTimestamp, toTimestamp]);

  useEffect(() => {
    if (!hasSyncedBonusType.current) {
      hasSyncedBonusType.current = true;
      return;
    }

    changeQuery({
      order: null,
      sort: null,
      type: bonusType,
    });
  }, [bonusType, changeQuery]);

  const bonusReportItems = data?.items.map((item) => ({ ...item, id: `bonus-report-${item.type}` }));
  const totalsRow = generateTotalsRow(data?.totals as BonusTotals | undefined);
  const aggregatedData = appendTotalsRow(bonusReportItems, totalsRow);

  const columns: GridColDef[] = useMemo(
    () => [
      createColumn('type', 'Bonus Type', {
        valueFormatter: (value, row, column, apiRef) => formatSnakeCaseString(value),
        sortable: false,
      }),
      createNumberColumn('distributedBonusAmount', 'Distributed', {
        sortable: false,
      }),
      createColumn('ticketCount', 'Tickets', {
        sortable: false,
      }),
      createNumberColumn('stakeAmount', 'Stakes', {
        sortable: false,
      }),
      createNumberColumn('winningAmount', 'Winnings', {
        sortable: false,
      }),
      createNumberColumn('profit', 'Profit', {
        sortable: false,
      }),
    ],
    []
  );

  return (
    <TableTemplate
      rows={aggregatedData}
      columns={columns}
      loading={isFetching || isLoading}
      rowCount={data?.count ?? 1}
      handlePaginationModelChange={updateQueryParams}
      getRowClassName={getTotalsRowClass}
      changeQuery={changeQuery}
      getRowId={(row) => row.id}
      mobileCardHeaderItems={getMobileCardHeaderItems as never}
      customTableHeaderContent={headerContent}
      showExport
    />
  );
};

export default CampaignBonusReportsPage;
